import { createApi } from '@reduxjs/toolkit/query/react';

import { axiosBaseQuery } from '../axiosBaseQuery';

export interface BaseResponse {
  IsFailure: boolean;
  IsSuccess: boolean;
}

export interface EmailExistResponse extends BaseResponse {
  Data: EmailExistResponseData;
}

export interface EmailExistResponseData {
  ExistInEam: boolean;
  ExistInSRS: boolean;
}

export interface EmailExistResponseData {
  ExistInEam: boolean;
  ExistInSRS: boolean;
}

export interface EmailStatus {
  existInEam: boolean;
  existInSRS: boolean;
}

export interface RegisterAnonymousUserRequest {
  firstName: string;
  lastName: string | null;
  hasFamilyName: boolean;
  dateOfBirth: string;
  email: string;
  confirmEmail: string;
}

export interface RegisterUserRequest extends RegisterAnonymousUserRequest {
  password: string;
  confirmPassword: string;
}

export interface RegisterLoggedUserRequest {
  firstName: string;
  lastName: string | null;
  hasFamilyName: boolean;
  dateOfBirth: string;
}

export interface InviteParentRequest extends RegisterAnonymousUserRequest {
  parentEmail: string;
  relationshipToChild: string;
}

export interface ActivateAccountRequest {
  email: string;
  token: string;
}

export interface ActivateAccountAndSetPasswordRequest extends ActivateAccountRequest {
  password: string;
  confirmPassword: string;
}

export interface CompleteMinorToAdultMigrationRequest {
  password: string;
  confirmPassword: string;
  candidateId: number;
  token: string;
}

export const signupApi = createApi({
  reducerPath: 'signupApi',
  baseQuery: axiosBaseQuery({
    baseUrl: '/web-api',
  }),
  endpoints: (builder) => ({
    checkEmail: builder.query<EmailStatus, string>({
      query: (email) => ({
        url: `/b2c/api/security/user/emailExist?email=${email}`,
        method: 'get',
      }),
      transformResponse: (response: EmailExistResponse, meta) =>
        response.IsSuccess
          ? {
              existInEam: response.Data.ExistInEam,
              existInSRS: response.Data.ExistInSRS,
            }
          : { existInEam: false, existInSRS: false },
    }),
    registerUser: builder.mutation<BaseResponse, RegisterUserRequest>({
      query: (data) => ({
        url: `/b2c/api/security/register/user`,
        method: 'post',
        data: data,
      }),
    }),
    registerLoggedUser: builder.mutation<BaseResponse, RegisterLoggedUserRequest>({
      query: (data) => ({
        url: `/b2c/api/security/register/candidate/logged`,
        method: 'post',
        data: data,
      }),
    }),
    sendAccountEmail: builder.mutation<BaseResponse, string>({
      query: (email) => ({
        url: `/b2c/api/security/user/sendAttemptAccountEmail`,
        method: 'post',
        data: {
          Email: email,
        },
      }),
    }),
    sendParentAccountInvitation: builder.mutation<void, InviteParentRequest>({
      query: (data) => ({
        url: `/api/account/sendParentCreateAccountInvitation`,
        method: 'post',
        data: data,
      }),
    }),
    registerAnonymousUser: builder.mutation<void, RegisterAnonymousUserRequest>({
      query: (data) => ({
        url: `/b2c/api/security/register/candidate/anonymous`,
        method: 'post',
        data: data,
      }),
    }),
    activateAccount: builder.mutation<void, ActivateAccountRequest>({
      query: (data) => ({
        url: `/b2c/api/security/activate/user`,
        method: 'post',
        data: data,
      }),
    }),
    activateAccountAndSetPassword: builder.mutation<void, ActivateAccountAndSetPasswordRequest>({
      query: (data) => ({
        url: `/b2c/api/security/activate/user/password`,
        method: 'post',
        data: data,
      }),
    }),
    activateAccountMigrate: builder.mutation<void, CompleteMinorToAdultMigrationRequest>({
      query: (data) => ({
        url: `/b2c/api/candidate/${data.candidateId}/completeMinorToAdultMigration`,
        method: 'post',
        data: data,
      }),
    }),
    accountMinorToAdultMigrate: builder.mutation<void, number>({
      query: (candidateId) => ({
        url: `/b2c/api/candidate/${candidateId}/completeMinorToAdultMigration/activated`,
        method: 'post'
      }),
    }),
  }),
});

export const {
  useLazyCheckEmailQuery,
  useRegisterUserMutation,
  useSendAccountEmailMutation,
  useSendParentAccountInvitationMutation,
  useRegisterAnonymousUserMutation,
  useActivateAccountMutation,
  useActivateAccountAndSetPasswordMutation,
  useRegisterLoggedUserMutation,
  useActivateAccountMigrateMutation,
  useAccountMinorToAdultMigrateMutation
} = signupApi;
